import { QRCodeSVG } from "qrcode.react";
import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CheckoutService, StorefrontService } from "src/app/services/StorefrontService";
import { Basket, money } from "./Basket";
import { clubTheme } from "./ClubTheme";
import "./Shop.css";
import { ShopHeader } from "./ShopHeader";

/** How long to keep asking before admitting we don't know yet. */
const GIVE_UP_AFTER_MS = 30000;
const POLL_EVERY_MS = 1500;

/**
 * What actually happened to an order.
 *
 * This polls the server rather than believing the redirect. Stripe sending a
 * browser back with redirect_status=succeeded means the card was authorised —
 * it does not mean our webhook has run and the order is Paid. Telling a
 * supporter they have a ticket before the system agrees is how you end up with
 * somebody at a turnstile holding a confirmation for an order that failed.
 *
 * If the webhook is slow we say so, rather than either lying or showing a
 * spinner for ever.
 */
export function ShopConfirmation() {
	const { slug = "", orderId = "" } = useParams();
	const [search] = useSearchParams();
	const navigate = useNavigate();
	const [club, setClub] = React.useState<any>(null);
	const [status, setStatus] = React.useState<any>(null);
	const [tickets, setTickets] = React.useState<any[]>([]);
	const [slow, setSlow] = React.useState(false);

	// The key that fetches the tickets. It arrives in the URL, but a bank's 3DS
	// page can drop the query string on the way back, so storage is the backup.
	const orderKey = React.useMemo(() => {
		const fromUrl = search.get("key");
		if (fromUrl) return fromUrl;
		try {
			return window.localStorage.getItem(`pb-order-${orderId}`) || "";
		} catch {
			return "";
		}
	}, [search, orderId]);

	React.useEffect(() => {
		// Whatever brought a fan here, the basket is spent.
		Basket.clear(slug);
	}, [slug]);

	React.useEffect(() => {
		let live = true;
		(async () => {
			try {
				const res = await StorefrontService.get(slug);
				if (live && res.ok) setClub((await res.json()).club);
			} catch { /* the header falls back to a plain title */ }
		})();
		return () => { live = false; };
	}, [slug]);

	React.useEffect(() => {
		let live = true;
		const startedAt = Date.now();
		let timer: any;

		const poll = async () => {
			try {
				const res = await CheckoutService.getOrder(Number(orderId));
				if (!live) return;

				if (res.ok) {
					const body = await res.json();
					setStatus(body);
					if (body.paid || body.status === "Failed" || body.status === "Cancelled") return;
				}
			} catch { /* keep trying */ }

			if (!live) return;
			if (Date.now() - startedAt > GIVE_UP_AFTER_MS) {
				setSlow(true);
				return;
			}
			timer = setTimeout(poll, POLL_EVERY_MS);
		};

		poll();
		return () => { live = false; clearTimeout(timer); };
	}, [orderId]);

	React.useEffect(() => {
		let live = true;
		if (!status?.paid || !orderKey || tickets.length) return;
		(async () => {
			try {
				const res = await CheckoutService.getTickets(Number(orderId), orderKey);
				if (!live || !res.ok) return;
				const body = await res.json();
				if (Array.isArray(body?.tickets)) setTickets(body.tickets);
			} catch {
				// The order still shows as paid; the tickets just aren't drawn.
			}
		})();
		return () => { live = false; };
	}, [status, orderKey, orderId, tickets.length]);

	const paid = status?.paid;
	const failed = status?.status === "Failed" || status?.status === "Cancelled";

	return (
		<div className="shop" style={clubTheme(club)}>
			<ShopHeader club={club} slug={slug} />
			<div className="shop-result">
				{paid && <>
					<span className="shop-tick">✓</span>
					<h2>You&rsquo;re in</h2>
					<p>
						Order #{orderId} is paid{status?.totalPence ? ` — ${money(status.totalPence)}` : ""}.
					</p>

					{tickets.length > 0 && <div className="shop-tickets">
						{tickets.map((t: any, index: number) => (
							<div className="shop-ticketcard" key={t.token}>
								<QRCodeSVG bgColor="#ffffff" fgColor="#111827" includeMargin size={168} value={t.token} />
								<strong>{t.ticketType || "Admission"}</strong>
								<span>Ticket {index + 1} of {tickets.length}</span>
							</div>
						))}
					</div>}

					<p className="shop-note">
						{tickets.length > 0
							? "Show these at the turnstile. Screenshot them — you won't need signal on the day."
							: "Your tickets are being prepared. Keep your order number to hand."}
					</p>
				</>}

				{!paid && failed && <>
					<h2>That payment didn&rsquo;t go through</h2>
					<p>Nothing has been taken. Give it another go, or try a different card.</p>
					<button className="shop-btn" onClick={() => navigate(`/club/${slug}`)} type="button">
						Back to what&rsquo;s on
					</button>
				</>}

				{!paid && !failed && !slow && <>
					<span className="shop-spinner" />
					<h2>Confirming your payment</h2>
					<p>This takes a few seconds. Don&rsquo;t close this page.</p>
				</>}

				{!paid && !failed && slow && <>
					<h2>Still confirming</h2>
					<p>
						Your payment has gone through but we&rsquo;re waiting on the confirmation.
						Keep order #{orderId} to hand and check back in a moment.
					</p>
					<button className="shop-btn secondary" onClick={() => window.location.reload()} type="button">
						Check again
					</button>
				</>}
			</div>
		</div>
	);
}
